import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { ResearchEntity } from "../../../types/researchWorkflow";

interface AddToShortListModalProps {
  open: boolean;
  approvedEntities: ResearchEntity[];
  onClose: () => void;
  onConfirm: (entityIds: string[]) => void;
}

const AddToShortListModal: React.FC<AddToShortListModalProps> = ({
  open,
  approvedEntities,
  onClose,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const handleToggle = (entityId: string) => {
    setSelectedIds((prev) =>
      prev.includes(entityId)
        ? prev.filter((id) => id !== entityId)
        : [...prev, entityId],
    );
  };

  const handleConfirm = () => {
    onConfirm(selectedIds);
    setSelectedIds([]);
    setSearchTerm("");
  };

  const handleClose = () => {
    setSelectedIds([]);
    setSearchTerm("");
    onClose();
  };

  const filteredEntities = approvedEntities.filter((entity) =>
    entity.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to Evaluation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select entities to add for deep research
        </Typography>

        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search entities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Entity List */}
        {filteredEntities.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              {searchTerm
                ? "No matching entities found"
                : "No entities available for evaluation"}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: "auto" }}>
            {filteredEntities.map((entity) => (
              <ListItem key={entity.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(entity.id)} dense>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedIds.includes(entity.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={entity.name}
                    secondary={`${entity.type} • ${
                      (
                        {
                          high_potential: "High",
                          medium_potential: "Medium",
                          low_potential: "Low",
                          irrelevant: "Irrelevant",
                        } as Record<string, string>
                      )[entity.matchCategory || ""] || "?"
                    } potential`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}

        {selectedIds.length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {selectedIds.length} entit{selectedIds.length === 1 ? "y" : "ies"}{" "}
            selected
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedIds.length === 0}
        >
          Add to Evaluation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToShortListModal;
